// Hana — words written in light: turns text (from text.js) into pen strokes the shader can follow.
// The text is drawn with the system font, thinned to a one-pixel skeleton, traced into strokes,
// put in a writing order (left to right, top to bottom), and resampled into GN points (x, y, 1 stroke / .5 cursive link, progress 0..1).
(function () {
  const H = window.HANA;
  const GN = H.GN = 160;
  const FONT = '"PingFang SC", "Heiti SC", "Apple SD Gothic Neo", "Hiragino Sans", "Noto Sans CJK SC", "Noto Sans KR", "Malgun Gothic", sans-serif';
  const PX = 180;

  const SCRIPT = '"Snell Roundhand", "Apple Chancery", "Zapfino", "Brush Script MT", cursive';   // classic cursive

  function raster(text, script) {
    const c = document.createElement('canvas'), g = c.getContext('2d');
    const font = script ? `bold ${PX}px ${SCRIPT}` : `500 ${PX}px ${FONT}`;
    g.font = font;
    const pad = PX * 0.25;
    const w = Math.ceil(g.measureText(text).width + pad * 2), h = Math.ceil(PX * 1.5);
    c.width = w; c.height = h;
    g.font = font; g.textBaseline = 'middle'; g.fillStyle = '#fff';
    g.fillText(text, pad, h / 2);
    const d = g.getImageData(0, 0, w, h).data, B = new Uint8Array(w * h);
    for (let i = 0; i < w * h; i++) B[i] = d[i * 4 + 3] > (script ? 60 : 110) ? 1 : 0;   // script hairlines are thin
    const chars = []; let x = pad;
    for (const ch of [...text]) { const cw = g.measureText(ch).width; chars.push([x, x + cw]); x += cw; }
    return { B, w, h, chars };
  }

  // Zhang–Suen thinning
  function thin(B, w, h) {
    const at = (x, y) => B[y * w + x];
    let changed = true;
    const del = [];
    while (changed) {
      changed = false;
      for (let step = 0; step < 2; step++) {
        del.length = 0;
        for (let y = 1; y < h - 1; y++) for (let x = 1; x < w - 1; x++) {
          if (!at(x, y)) continue;
          const p = [at(x, y - 1), at(x + 1, y - 1), at(x + 1, y), at(x + 1, y + 1), at(x, y + 1), at(x - 1, y + 1), at(x - 1, y), at(x - 1, y - 1)];
          const n = p.reduce((a, b) => a + b, 0);
          if (n < 2 || n > 6) continue;
          let a = 0;
          for (let i = 0; i < 8; i++) if (!p[i] && p[(i + 1) % 8]) a++;
          if (a !== 1) continue;
          if (step === 0 ? (p[0] * p[2] * p[4] || p[2] * p[4] * p[6]) : (p[0] * p[2] * p[6] || p[0] * p[4] * p[6])) continue;
          del.push(y * w + x);
        }
        for (const i of del) B[i] = 0;
        if (del.length) changed = true;
      }
    }
  }

  const N8 = [[1, 0], [0, 1], [-1, 0], [0, -1], [1, 1], [-1, 1], [-1, -1], [1, -1]];  // 4-neighbours first

  function trace(B, w, h) {
    const on = (x, y) => x >= 0 && y >= 0 && x < w && y < h && B[y * w + x];
    const nbrs = (x, y) => N8.filter(([dx, dy]) => on(x + dx, y + dy)).map(([dx, dy]) => [x + dx, y + dy]);
    // crossing number: how many separate arms leave a pixel (a staircase step counts as one arm)
    const RING = [[1, 0], [1, 1], [0, 1], [-1, 1], [-1, 0], [-1, -1], [0, -1], [1, -1]];
    const arms = (x, y) => { let a = 0; for (let i = 0; i < 8; i++) { const [dx, dy] = RING[i], [ex, ey] = RING[(i + 1) % 8]; if (!on(x + dx, y + dy) && on(x + ex, y + ey)) a++; } return a; };
    const isNode = (x, y) => { const k = arms(x, y); return k === 1 || k >= 3 || nbrs(x, y).length === 1; };
    const seen = new Uint8Array(w * h);
    const segs = [];
    function walk(x0, y0, x1, y1) {
      const pts = [[x0, y0], [x1, y1]];
      let px = x0, py = y0, x = x1, y = y1;
      while (!isNode(x, y) || pts.length === 2 && isNode(x, y) && false) {
        seen[y * w + x] = 1;
        const nx = nbrs(x, y).filter(([a, b]) => !(a === px && b === py) && !seen[b * w + a]);
        if (!nx.length) break;
        const node = nx.find(([a, b]) => isNode(a, b));
        const [a, b] = nx.find(([a, b]) => !isNode(a, b)) || node;
        px = x; py = y; x = a; y = b;
        pts.push([x, y]);
      }
      return pts;
    }
    for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
      if (!on(x, y) || !isNode(x, y)) continue;
      for (const [a, b] of nbrs(x, y)) {
        if (seen[b * w + a]) continue;
        if (isNode(a, b)) { continue; }
        const pts = walk(x, y, a, b);
        if (pts.length > 3) segs.push(pts);
      }
    }
    // closed loops (an O, the circle of ㅎ)
    for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
      if (!on(x, y) || seen[y * w + x] || isNode(x, y)) continue;
      const pts = [[x, y]];
      seen[y * w + x] = 1;
      let cx = x, cy = y;
      for (;;) {
        const nx = nbrs(cx, cy).filter(([a, b]) => !seen[b * w + a]);
        if (!nx.length) break;
        [cx, cy] = nx[0]; seen[cy * w + cx] = 1; pts.push([cx, cy]);
      }
      if (pts.length > 12) { pts.push(pts[0]); pts.loop = true; segs.push(pts); }
    }
    return segs;
  }

  const len = pts => { let L = 0; for (let i = 1; i < pts.length; i++) L += Math.hypot(pts[i][0] - pts[i - 1][0], pts[i][1] - pts[i - 1][1]); return L; };

  // join pieces that meet at a junction and carry on in the same direction (a crossing is two strokes, not four)
  function join(segs) {
    const key = p => `${Math.round(p[0] / 4)},${Math.round(p[1] / 4)}`;
    const dirAt = (s, end) => {
      const a = end ? s[s.length - 1] : s[0], b = end ? s[Math.max(0, s.length - 16)] : s[Math.min(s.length - 1, 15)];
      const dx = a[0] - b[0], dy = a[1] - b[1], l = Math.hypot(dx, dy) || 1;
      return [dx / l, dy / l];
    };
    const near = (p, q) => Math.hypot(p[0] - q[0], p[1] - q[1]) < 7;
    // drop tiny spurs that hang off a junction
    const ends = [];
    segs.forEach((s, i) => { if (!s.loop) { ends.push({ i, end: 0, p: s[0] }); ends.push({ i, end: 1, p: s[s.length - 1] }); } });
    const linked = e => ends.some(f => f.i !== e.i && near(e.p, f.p));
    segs = segs.filter((s, i) => s.loop || len(s) > PX * 0.07 || (linked(ends.find(e => e.i === i && e.end === 0)) && linked(ends.find(e => e.i === i && e.end === 1))));
    let merged = true;
    while (merged) {
      merged = false;
      outer:
      for (let i = 0; i < segs.length; i++) for (let j = 0; j < segs.length; j++) {
        if (i === j || segs[i].loop || segs[j].loop) continue;
        for (const ei of [0, 1]) for (const ej of [0, 1]) {
          const pi = ei ? segs[i][segs[i].length - 1] : segs[i][0], pj = ej ? segs[j][segs[j].length - 1] : segs[j][0];
          if (!near(pi, pj)) continue;
          const di = dirAt(segs[i], ei), dj = dirAt(segs[j], ej);
          if (di[0] * dj[0] + di[1] * dj[1] > -0.86) continue;     // must run straight through
          const a = ei ? segs[i] : segs[i].slice().reverse();
          const b = ej ? segs[j].slice().reverse() : segs[j];
          const m = a.concat(b);
          segs = segs.filter((_, k) => k !== i && k !== j);
          segs.push(m);
          merged = true;
          break outer;
        }
      }
    }
    void key;
    return segs;
  }

  function smooth(pts) {
    return pts.map((p, i) => {
      if (i === 0 || i === pts.length - 1) return p;
      let x = 0, y = 0, n = 0;
      for (let k = -6; k <= 6; k++) { const q = pts[Math.min(pts.length - 1, Math.max(0, i + k))]; x += q[0]; y += q[1]; n++; }
      return [x / n, y / n];
    });
  }

  // text -> GN samples of (x, y, pen down 1 / lifted 0), scaled to fit maxW x maxH world units, centred
  H.glyph = function (text, maxW, maxH, script) {
    const R = raster(text, script);
    thin(R.B, R.w, R.h);
    let strokes = join(trace(R.B, R.w, R.h)).map(s => {
      const loop = s.loop;
      s = smooth(s);
      if (loop) {                       // start a loop at its top and go left (anticlockwise on screen)
        let top = 0;
        s.forEach((p, i) => { if (p[1] < s[top][1]) top = i; });
        s = s.slice(top).concat(s.slice(1, top + 1));
        if (s[Math.min(4, s.length - 1)][0] > s[0][0]) s.reverse();
      } else if (s[0][0] + s[0][1] > s[s.length - 1][0] + s[s.length - 1][1]) s.reverse();   // left to right, top to bottom
      return s;
    }).filter(s => len(s) > PX * 0.05);
    const charOf = s => { const mx = s.reduce((a, p) => a + p[0], 0) / s.length; let k = 0; R.chars.forEach(([a, b], i) => { if (mx >= a) k = i; }); return k; };
    const em = PX;
    strokes.forEach(s => { s.char = charOf(s); const [a, b] = R.chars[s.char]; s.key = (s[0][0] - a) / em + (s[0][1]) / em; });
    strokes.sort((a, b) => a.char - b.char || a.key - b.key);
    // like a hand writing cursive: within each character, after the first stroke, go to whichever stroke
    // starts nearest the pen (either end), leaning toward the usual top-left-first order
    const ordered = [];
    for (let ch = 0; ch < R.chars.length; ch++) {
      const left = strokes.filter(s => s.char === ch);
      if (!left.length) continue;
      let cur = left.shift();
      ordered.push(cur);
      while (left.length) {
        const pen = cur[cur.length - 1];
        let best = 0, flip = false, bestCost = 1e9;
        left.forEach((s, i) => {
          const d0 = Math.hypot(s[0][0] - pen[0], s[0][1] - pen[1]), d1 = Math.hypot(s[s.length - 1][0] - pen[0], s[s.length - 1][1] - pen[1]);
          const bias = s.key * em * .35;
          if (d0 + bias < bestCost) { bestCost = d0 + bias; best = i; flip = false; }
          if (d1 + bias < bestCost) { bestCost = d1 + bias; best = i; flip = true; }
        });
        cur = left.splice(best, 1)[0];
        if (flip) { const c = cur.char, k = cur.key; cur = cur.slice().reverse(); cur.char = c; cur.key = k; }
        ordered.push(cur);
      }
    }
    strokes = ordered;
    // fit
    let x0 = 1e9, x1 = -1e9, y0 = 1e9, y1 = -1e9;
    strokes.forEach(s => s.forEach(([x, y]) => { x0 = Math.min(x0, x); x1 = Math.max(x1, x); y0 = Math.min(y0, y); y1 = Math.max(y1, y); }));
    const k = Math.min(maxW / Math.max(x1 - x0, 1), maxH / Math.max(y1 - y0, 1));
    const cx = (x0 + x1) / 2, cy = (y0 + y1) / 2;
    const W = ([x, y]) => [(x - cx) * k, -(y - cy) * k];
    // cursive: one unbroken line. Each stroke flows into the next through a thin curved link that leaves
    // along the stroke's last direction and arrives along the next one's first, so the pen never lifts.
    const SLANT = script ? 0 : 0.16;               // a script font already leans                                 // a little forward lean
    const ws = strokes.map(s => s.map(W).map(([x, y]) => [x + y * SLANT, y]));
    const dirOf = (s, end) => {
      const a = end ? s[s.length - 1] : s[0], b = end ? s[Math.max(0, s.length - 6)] : s[Math.min(s.length - 1, 5)];
      const dx = end ? a[0] - b[0] : b[0] - a[0], dy = end ? a[1] - b[1] : b[1] - a[1], l = Math.hypot(dx, dy) || 1;
      return [dx / l, dy / l];
    };
    const pieces = [];                                  // { pts, kind: 1 stroke, .5 link }
    ws.forEach((s, i) => {
      if (i > 0) {
        const A = ws[i - 1], pa = A[A.length - 1], pb = s[0], ta = dirOf(A, 1), tb = dirOf(s, 0);
        const d = Math.hypot(pb[0] - pa[0], pb[1] - pa[1]);
        const ux = (pb[0] - pa[0]) / (d || 1), uy = (pb[1] - pa[1]) / (d || 1);
        // a link that has to double back stays tight, so it doesn't swing a loop around the letter
        const ha = d * (ta[0] * ux + ta[1] * uy > 0 ? .3 : .08), hb = d * (tb[0] * ux + tb[1] * uy > 0 ? .3 : .08);
        const P1 = [pa[0] + ta[0] * ha, pa[1] + ta[1] * ha], P2 = [pb[0] - tb[0] * hb, pb[1] - tb[1] * hb];
        const link = [];
        for (let q = 0; q <= 16; q++) {
          const u = q / 16, v = 1 - u;
          link.push([v * v * v * pa[0] + 3 * v * v * u * P1[0] + 3 * v * u * u * P2[0] + u * u * u * pb[0],
                     v * v * v * pa[1] + 3 * v * v * u * P1[1] + 3 * v * u * u * P2[1] + u * u * u * pb[1]]);
        }
        pieces.push({ pts: link, kind: .5 });
      }
      pieces.push({ pts: s, kind: 1 });
    });
    const lens = pieces.map(p => len(p.pts)), total = lens.reduce((a, b) => a + b, 0);
    const counts = lens.map(l => Math.max(3, Math.round((GN - 1) * l / total)));
    while (counts.reduce((a, b) => a + b, 0) > GN - 1) counts[counts.indexOf(Math.max(...counts))]--;
    const pts = [];                                    // [x, y, kind of the segment arriving here, distance]
    let D = 0;
    pieces.forEach((pc, i) => {
      const s = pc.pts;
      const c = [0]; for (let j = 1; j < s.length; j++) c.push(c[j - 1] + Math.hypot(s[j][0] - s[j - 1][0], s[j][1] - s[j - 1][1]));
      for (let q = i === 0 ? 0 : 1; q <= counts[i]; q++) {
        const target = c[c.length - 1] * q / counts[i];
        let j = 1; while (j < c.length - 1 && c[j] < target) j++;
        const f = (target - c[j - 1]) / Math.max(c[j] - c[j - 1], 1e-6);
        const x = s[j - 1][0] + (s[j][0] - s[j - 1][0]) * f, y = s[j - 1][1] + (s[j][1] - s[j - 1][1]) * f;
        if (pts.length) { const last = pts[pts.length - 1]; D += Math.hypot(x - last[0], y - last[1]); }
        pts.push([x, y, pc.kind, D]);
      }
    });
    const out = new Float32Array(GN * 4);
    for (let i = 0; i < GN; i++) {
      const p = pts[Math.min(i, pts.length - 1)];
      out[i * 4] = p[0]; out[i * 4 + 1] = p[1]; out[i * 4 + 2] = p[2]; out[i * 4 + 3] = D > 0 ? p[3] / D : 0;
    }
    if (pts.length < GN) for (let i = pts.length; i < GN; i++) out[i * 4 + 3] = 1 + i * 1e-4;   // padding past the end
    return out;
  };
})();
